import {createContext, useContext, useEffect, useLayoutEffect} from 'react'
import type {PropsWithChildren, JSX} from 'react'
import {setPageContext} from '@/lib/pageContext/page-context-store'
import type {PageContext} from '@/lib/pageContext/page-context-store'

const usePageContextStoreSyncEffect =
	typeof window === 'undefined' ? useEffect : useLayoutEffect

type PageContextProviderProps = PropsWithChildren<{
	value: PageContext
}>

// React components should read page metadata from context so the value follows the
// component tree and participates in normal React rendering.
const PageContextReactContext = createContext<PageContext | null>(null)

export const usePageContext = (): PageContext => {
	const context = useContext(PageContextReactContext)

	if (!context) {
		throw new Error(
			'usePageContext must be used within a PageContextProvider'
		)
	}

	return context
}

// This provider has two jobs:
// 1. Provide page context to React components via usePageContext.
// 2. Mirror the same value into a module store for code that runs outside React.
export const PageContextProvider = ({
	value,
	children,
}: PageContextProviderProps): JSX.Element => {
	// Sync before passive effects so callbacks registered by useReportWebVitals
	// can read the current page context on the first page load.
	usePageContextStoreSyncEffect(() => {
		setPageContext(value)
	}, [value])

	return (
		<PageContextReactContext.Provider value={value}>
			{children}
		</PageContextReactContext.Provider>
	)
}
